import { IconProps } from '@chakra-ui/icons';
import { ComponentWithAs } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { SmartDimmerSwitchIcon, SmartLightSwitchIcon, SmartOutletIcon } from '../../../styles/custom-icons';
import { useGetDeviceQuery } from '../devicesApi';
import { useGetLiveSwitchQuery, useGetSwitchQuery } from '../switches/switchApi';

/**
 * Combines switch, appliance and live switch data for Ayla devices
 *
 * @param switchId - The switch ID to fetch data for
 * @param shouldIncludeLocalMatterDeviceData - Whether to include fetching local Matter device data
 * @returns Consolidated Ayla switch data with loading states for the various sources
 */
export function useAylaSwitchData(switchId: number) {
  const {
    data: liveSwitch,
    isLoading: isLiveDataLoading,
    isError: isLiveSwitchError,
  } = useGetLiveSwitchQuery(switchId);
  const { data: apiSwitch, isLoading: isSwitchLoading, isError: isApiSwitchError } = useGetSwitchQuery(switchId);
  const { data: device, isLoading: isDeviceLoading } = useGetDeviceQuery({ switchId });

  return {
    data: {
      ...apiSwitch,
      ...liveSwitch,
      ...device,
    },
    isLoading: isSwitchLoading || isDeviceLoading,
    isLiveDataLoading,
    isLiveSwitchError,
    isApiSwitchError,
  };
}

export type AylaDeviceType = 'AYLA_SWITCH' | 'AYLA_OUTLET' | 'AYLA_DIMMER';
type AylaDeviceTypeInfo = {
  deviceType: AylaDeviceType;
  isMatterDevice: boolean;
};

/**
 * Returns the device type for the given appliance `display_name`. Since we don't really have a way to differentiate --
 * -- between Ayla device types, we must rely on this field from the /appliances API.
 *
 * @param displayName - The `display_name` of the appliance
 * @returns A device type, and a boolean indicating if the device uses Matter or not
 */
export function getAylaDeviceType(displayName?: string): AylaDeviceTypeInfo {
  const isMatterDevice = displayName?.includes('Matter') || false;

  let deviceType: AylaDeviceType = 'AYLA_OUTLET';
  if (displayName?.includes('SE Switch')) deviceType = 'AYLA_SWITCH';
  if (displayName?.includes('SE Dimmer')) deviceType = 'AYLA_DIMMER';

  return { deviceType, isMatterDevice };
}

type AylaDeviceUIConfig = Record<
  AylaDeviceType,
  {
    description: string;
    icon: ComponentWithAs<'svg', IconProps>;
  }
>;

export function useTranslatedUIConfig(): AylaDeviceUIConfig {
  const { t } = useTranslation();
  return {
    AYLA_SWITCH: {
      description: t('Devices.ayla switch description'),
      icon: SmartLightSwitchIcon,
    },
    AYLA_OUTLET: {
      description: t('Devices.ayla outlet description'),
      icon: SmartOutletIcon,
    },
    AYLA_DIMMER: {
      description: t('Devices.ayla dimmer description'),
      icon: SmartDimmerSwitchIcon,
    },
  };
}
